import { useEffect, useMemo, useState } from "react";
import {
  createScene,
  productionPage,
  fontManifestDigest,
  type Project,
  type Values,
  type Context,
  type OutputProfile,
  type FontRuntimeMap,
} from "@proof/core";
import {
  loadBrowserFonts,
  defaultFontLoader,
  type FontLoader,
} from "@proof/fonts/browser";
export type { FontLoader } from "@proof/fonts/browser";
export function useDocumentScene(
  project: Project,
  values: Values,
  partId?: string,
  context?: Context,
  profile?: OutputProfile,
  loadFont: FontLoader = defaultFontLoader,
  showProduction = false,
) {
  const digest = fontManifestDigest(project.fontManifest),
    [state, setState] = useState<{
      digest: string;
      loader: FontLoader;
      fonts?: FontRuntimeMap;
      error?: string;
    }>({ digest: "", loader: loadFont });
  useEffect(() => {
    if (!project.fontManifest.length) return;
    const controller = new AbortController();
    let dispose: (() => void) | undefined;
    setState({ digest, loader: loadFont });
    void loadBrowserFonts(project.fontManifest, loadFont, controller.signal)
      .then((fonts) => {
        if (controller.signal.aborted) fonts.dispose();
        else {
          dispose = fonts.dispose;
          setState({ digest, loader: loadFont, fonts });
        }
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setState({ digest, loader: loadFont, error: (e as Error).message });
      });
    return () => {
      controller.abort();
      dispose?.();
    };
  }, [digest, loadFont]);
  const loading =
    !!project.fontManifest.length &&
    (state.digest !== digest ||
      state.loader !== loadFont ||
      (!state.fonts && !state.error));
  return useMemo(() => {
    if (loading) return { loading: true };
    if (
      state.digest === digest &&
      state.loader === loadFont &&
      state.error &&
      project.fontManifest.length
    )
      return { loading: false, error: state.error };
    try {
      const scene = createScene(
        project,
        values,
        partId,
        context,
        profile,
        state.fonts,
      );
      return {
        loading: false,
        fonts: state.fonts,
        scene: showProduction
          ? { ...scene, pages: scene.pages.map(productionPage) }
          : scene,
      };
    } catch (e) {
      return { loading: false, error: (e as Error).message };
    }
  }, [
    project,
    values,
    partId,
    context,
    profile,
    state,
    loading,
    digest,
    showProduction,
  ]);
}
